/** The compliance checklist for one filing obligation — **gate 26**.
 *
 * Everything the checklist says comes from a row that already reached the browser, and this file is
 * the one place the assembly happens so a screen cannot hold a second answer. The inputs are the two
 * reads the Reports screen already makes: `ObligationOut` (the determination, its date, its basis,
 * its assurance position) and `ReportOut` (the disclosures, the checks that did not pass, the
 * verdict). **No route was added**; nothing here fetches.
 *
 * ## The rules this file keeps
 *
 * - **Every sentence is a row's.** An item's `text` and `notes` are copied from a field of the
 *   payload and never reworded, and `trace` names which field, which row key and which citation.
 *   There is no regulatory sentence in this file, no threshold and no date. The only words written
 *   here are item *titles* for the two items that have no row of their own (the verdict and the
 *   check count), and those are labels, not claims.
 * - **An item the product cannot check is shown, as not checked.** A disclosure line with no figure
 *   is `open`, never absent. A regime with no checks loaded, a determination missing a fact, an
 *   assurance position under dispute and a date inside a regulation that is not in force are all
 *   `unchecked` — the same direction as `validation.Verdict`, where absence never reads as success.
 * - **`met` is a claim about one row, and nothing is `met` at the top.** The verdict item is never
 *   `met`: *checks passed* is not approval and not a statement that a filing is lawfully fit to file
 *   (F07). The strongest status it can take is `person`, which says whose judgement is left. That
 *   is pinned for all six verdicts and for a verdict this file has never heard of.
 * - **A date is never shown without its basis, and never counted down to.** The deadline item
 *   carries `due_date_basis` verbatim and, when the regime is not presently operative, the label
 *   and caveat that say so (C-03, C-07). This file takes no clock; there is no way for it to say
 *   "N days left" about a date that may never become law.
 * - **A filing nobody owes has no outstanding work.** `FilingStatus` is *"whether an entity has to
 *   file, and if not, why not"*, so for `not_in_scope`, `exempt` and `satisfied_by_parent` an `open`
 *   item is demoted to `noted` — shown, because hiding it would read as nothing being required, but
 *   not counted as work. An `undetermined` determination is **not** demoted: the missing fact could
 *   have changed the answer, which is the whole of what `undetermined` means.
 */

import type { Finding, Obligation, Report, ReportLine } from "../api/queries";

export type SectionId =
  | "applicability"
  | "deadline"
  | "disclosures"
  | "checks"
  | "assurance"
  | "standing";

/** Where an item stands, in the fewest words that keep the five cases apart.
 *
 * - `met` — the one row this item is about is satisfied. Never used for the verdict.
 * - `open` — outstanding work; counted.
 * - `unchecked` — EmitIQ did not or cannot check this. Not a pass.
 * - `noted` — information with no action in it: a date, a reason, a position.
 * - `person` — everything EmitIQ can check has been; what remains is somebody's judgement.
 */
export type Status = "met" | "open" | "unchecked" | "noted" | "person";

/** Which field of which payload an item was read from, and the row it names. */
export interface Trace {
  /** The payload field. The row behind it is: `obligation.reasoning` ->
   * `regime_applicability_rule`; `report.lines` -> `regime_disclosure`; `report.findings` ->
   * `regime_validation_rule` (or a withheld-site finding, which has no rule row and says so in its
   * own citation); `obligation.assurance` -> `regime_assurance_rule`; `obligation.due_date_basis`
   * -> `filing_obligation`, which stores the deadline rule's sentence. */
  from:
    | "obligation.reasoning"
    | "obligation.due_date_basis"
    | "obligation.assurance"
    | "report.lines"
    | "report.findings";
  /** The row's key (a rule key, a disclosure key, a finding key), or the obligation's id. */
  key: string;
  /** The paragraph, verbatim. Null where the payload carries none. */
  citation: string | null;
  sourceUrl: string | null;
}

export interface Note {
  /** What the sentence is, so a screen can label it without parsing it. */
  kind:
    | "detail"
    | "remedy"
    | "dispute"
    | "missing"
    | "limitation"
    | "unavailable"
    | "first_cycle_year";
  text: string;
}

export interface Figure {
  value: string;
  unit: string | null;
  records: number | null;
}

export interface Item {
  /** Stable across renders and unique in the checklist: `<section>:<key>`. */
  id: string;
  section: SectionId;
  status: Status;
  title: string;
  /** A raw code from the payload (a status, a verdict, a severity, a level, an ISO date), for the
   * screen to name in its own vocabulary. Never a sentence. */
  code: string | null;
  /** The row's own sentence, verbatim. */
  text: string | null;
  notes: Note[];
  figure: Figure | null;
  /** Present on the deadline item exactly when the regime is not presently operative. */
  qualifier: { label: string | null; caveat: string } | null;
  /** Null where no paragraph stands behind the item — the unexplained differences, the check
   * count and the verdict, which are EmitIQ's own and must not borrow a citation. */
  trace: Trace | null;
}

export interface Section {
  id: SectionId;
  items: Item[];
}

export type Owed = "owed" | "not_owed" | "unknown";

export interface Checklist {
  obligationId: string;
  owed: Owed;
  /** The regime's own caveat, once, above everything. Null for a regime that is law. */
  inForce: { label: string | null; caveat: string } | null;
  sections: Section[];
}

/** The verdicts, and what each can be at the very most. `checks_passed` is `person` and not `met`:
 * that is the line this file exists not to cross. */
const VERDICT_STATUS: Record<string, Status> = {
  not_evaluated: "unchecked",
  unsupported: "open",
  incomplete: "open",
  checks_passed: "person",
  approved: "person",
  issued: "person",
};

const NOT_OWED_STATUSES: ReadonlySet<string> = new Set([
  "not_in_scope",
  "exempt",
  "satisfied_by_parent",
]);

export function owedOf(obligation: Pick<Obligation, "status" | "undetermined">): Owed {
  // Undetermined first: a negative status over a missing fact is not settled.
  if (obligation.undetermined) return "unknown";
  if (obligation.status === "required") return "owed";
  return NOT_OWED_STATUSES.has(obligation.status) ? "not_owed" : "unknown";
}

function note(kind: Note["kind"], text: string | null | undefined): Note[] {
  return text ? [{ kind, text }] : [];
}

function item(partial: Pick<Item, "id" | "section" | "status" | "title"> & Partial<Item>): Item {
  return {
    code: null,
    text: null,
    notes: [],
    figure: null,
    qualifier: null,
    trace: null,
    ...partial,
  };
}

function applicability(obligation: Obligation): Item[] {
  const items: Item[] = [
    item({
      id: "applicability:status",
      section: "applicability",
      title: "Determination",
      code: obligation.status,
      // A negative status over a fact nobody recorded is not shown as settled.
      status: obligation.undetermined ? "unchecked" : "noted",
    }),
  ];
  if (obligation.superseded_by_obligation_id) {
    items.push(
      item({
        id: "applicability:superseded",
        section: "applicability",
        title: "Superseded by a newer determination",
        code: obligation.superseded_by_obligation_id,
        status: "noted",
      }),
    );
  }
  for (const line of obligation.reasoning) {
    items.push(
      item({
        id: `applicability:${line.rule}`,
        section: "applicability",
        title: line.rule,
        code: line.satisfied,
        text: line.statement,
        // A contested rule and a missing fact are both "we cannot say yet".
        status: line.missing || line.dispute ? "unchecked" : "noted",
        notes: [...note("dispute", line.dispute), ...note("missing", line.missing)],
        trace: {
          from: "obligation.reasoning",
          key: line.rule,
          citation: line.citation,
          sourceUrl: line.source_url,
        },
      }),
    );
  }
  return items;
}

function deadline(obligation: Obligation): Item {
  const qualifier = obligation.in_force_caveat
    ? { label: obligation.in_force_label, caveat: obligation.in_force_caveat }
    : null;
  return item({
    id: "deadline:due",
    section: "deadline",
    title: "Filing date",
    code: obligation.due_date,
    // The basis is the row's sentence and is always present: a date without it is a figure without
    // a receipt, and so is a blank where a date should be. Not in force => not established.
    text: obligation.due_date_basis,
    status: qualifier ? "unchecked" : "noted",
    qualifier,
    trace: {
      from: "obligation.due_date_basis",
      key: obligation.obligation_id,
      citation: null,
      sourceUrl: null,
    },
  });
}

function disclosure(line: ReportLine): Item {
  const present = line.value !== null;
  // A guidance-only line is not the regulation's requirement (B-19), so it is information and not
  // work, whether or not a figure exists. A regulation line with no figure is open — whether
  // EmitIQ cannot produce it or is waiting for an input, which the payload does not distinguish.
  const status: Status = line.basis === "guidance" ? "noted" : present ? "met" : "open";
  return item({
    id: `disclosures:${line.key}`,
    section: "disclosures",
    title: line.label,
    code: line.basis,
    text: line.requirement,
    status,
    // A narrative line's value is the company's own words; the checklist records that it exists.
    figure:
      present && line.measure !== "narrative"
        ? { value: line.value as string, unit: line.unit, records: line.records }
        : null,
    notes: [...note("unavailable", line.unavailable_because), ...note("limitation", line.limitation)],
    trace: {
      from: "report.lines",
      key: line.key,
      citation: line.citation,
      sourceUrl: line.source_url,
    },
  });
}

function finding(f: Finding): Item {
  return item({
    id: `checks:${f.key}`,
    section: "checks",
    title: f.message,
    code: f.severity,
    text: f.message,
    // `blocking` stops a filing; `advisory` is read rather than fixed.
    status: f.severity === "blocking" ? "open" : "noted",
    notes: [...note("detail", f.detail), ...note("remedy", f.remedy)],
    trace: {
      from: "report.findings",
      key: f.key,
      citation: f.citation,
      sourceUrl: f.source_url,
    },
  });
}

function checks(report: Report): Item[] {
  const items: Item[] = [
    item({
      id: "checks:run",
      section: "checks",
      title: "Checks this regime defines",
      code: String(report.rules_evaluated),
      // Zero rules is the finding: nothing was checked, and that never reads as a pass. A non-zero
      // count is *information* — it is deliberately not `met`, because findings are only what
      // failed and the census, the boundary and the reconciliation hold a filing without one.
      status: report.rules_evaluated === 0 ? "unchecked" : "noted",
    }),
    ...report.findings.map(finding),
  ];
  for (const difference of report.unreconciled ?? []) {
    items.push(
      item({
        id: `checks:unreconciled:${difference.disclosure_key}`,
        section: "checks",
        title: difference.disclosure_key,
        code: "unexplained_difference",
        status: "open",
        figure: { value: difference.delta, unit: difference.unit, records: null },
        // No paragraph of either regime asks for this, so there is no trace to borrow.
        trace: null,
      }),
    );
  }
  return items;
}

function assurance(obligation: Obligation): Item[] {
  if (obligation.assurance.length === 0) {
    // Empty is "nothing sourced", not "none required" (B-18).
    return [
      item({
        id: "assurance:none-sourced",
        section: "assurance",
        title: "Assurance",
        status: "unchecked",
      }),
    ];
  }
  return obligation.assurance.map((rule) =>
    item({
      id: `assurance:${rule.key}`,
      section: "assurance",
      title: rule.description,
      code: rule.level,
      text: rule.description,
      // Null level or a standing dispute: the sources disagree or say nothing, and the product
      // states the question rather than an answer.
      status: rule.dispute || rule.level === null ? "unchecked" : "noted",
      notes: [
        ...note("dispute", rule.dispute),
        ...note("limitation", rule.limitation),
        ...note("first_cycle_year", rule.first_cycle_year?.toString()),
      ],
      trace: {
        from: "obligation.assurance",
        key: rule.key,
        citation: rule.citation,
        sourceUrl: rule.source_url,
      },
    }),
  );
}

function standing(report: Report): Item {
  return item({
    id: "standing:verdict",
    section: "standing",
    title: "Where this filing stands",
    code: report.verdict,
    // Verbatim. For `checks_passed` it carries the sentence that says that is not approval.
    text: report.verdict_reason,
    // An unrecognised verdict is not a pass.
    status: VERDICT_STATUS[report.verdict] ?? "unchecked",
  });
}

/** The reads a section needs and does not have. One `unchecked` item, never an empty section. */
function notRead(section: SectionId, title: string): Item {
  return item({ id: `${section}:not-read`, section, status: "unchecked", title });
}

/** Assemble the checklist. `report` is `undefined` while the report is loading or when its read
 * failed; the three sections that need it then say so as `unchecked` instead of vanishing. */
export function buildChecklist(obligation: Obligation, report: Report | undefined): Checklist {
  const owed = owedOf(obligation);
  const inForce = obligation.in_force_caveat
    ? { label: obligation.in_force_label, caveat: obligation.in_force_caveat }
    : null;

  const sections: Section[] = [
    { id: "applicability", items: applicability(obligation) },
    { id: "deadline", items: [deadline(obligation)] },
    {
      id: "disclosures",
      items: report
        ? report.lines.map(disclosure)
        : [notRead("disclosures", "Required disclosures")],
    },
    { id: "checks", items: report ? checks(report) : [notRead("checks", "Checks")] },
    { id: "assurance", items: assurance(obligation) },
    { id: "standing", items: [report ? standing(report) : notRead("standing", "Where this filing stands")] },
  ];

  // A filing nobody owes has no outstanding work (see the header). Demoted, never removed.
  if (owed === "not_owed") {
    for (const section of sections) {
      section.items = section.items.map((i) => (i.status === "open" ? { ...i, status: "noted" } : i));
    }
  }

  return { obligationId: obligation.obligation_id, owed, inForce, sections };
}

/** How many items stand at each status. Counts only — there is deliberately no `complete`. */
export function tally(checklist: Checklist): Record<Status, number> {
  const counts: Record<Status, number> = { met: 0, open: 0, unchecked: 0, noted: 0, person: 0 };
  for (const section of checklist.sections) {
    for (const i of section.items) counts[i.status] += 1;
  }
  return counts;
}
